import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { getJobOrders } from '../API/jobOrderAPI';
import { getTechnicians } from "../API/technicianAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { fetchAndFilterJOsBySingleDate } from '../JobOrders/utils/JobOrderFilter';
import { groupByEndorsementIdAndSort } from '../EndorsementForm/utils/EndorsementGrouping';

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45",
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  opacity: 0.8,
}));

const convertToAmPm = (time24) => {
  const [hour, minute] = time24.split(':');
  const hourNum = parseInt(hour, 10);
  const suffix = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = hourNum % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
};

const formatJobDateToDDMMMYYYY = (dateString) => {
  const dateObj = new Date(dateString);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthAbbrev = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  return `${day}-${monthAbbrev}-${year}`;
};

function ViewDailyForecast() {
  const [dateFilter, setDateFilter] = useState();
  const [searchedDate, setSearchedDate] = useState(null);
  const [groupedList, setGroupedList] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [showRestDayCard, setShowRestDayCard] = useState(false);
  const [totalJobOrders, setTotalJobOrders] = useState(0);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch technicians on component mount
  useEffect(() => {
    (async () => {
      setTechLoading(true);
      try {
        const fetchedTechnicians = await getTechnicians();
        setTechnicians(fetchedTechnicians);
      } catch (error) {
        console.error("Error fetching technicians", error);
      }
      setTechLoading(false);
    })();
  }, []);

  const handleSearchButtonClick = async () => {
    setIsLoading(true);

    if (dateFilter) {
      setSearchedDate(dateFilter);

      // Use the shared single-date helper (already does the 5:00→4:59 window)
      // We want ALL job orders for the day (no status/SR filters), so pass empty arrays.
      const { list } = await fetchAndFilterJOsBySingleDate({
        date: dateFilter,
        getJobOrdersApi: getJobOrders,
        status: [],                // include all statuses
        serviceReportStatus: [],   // include all SR statuses
        billingRecordStatus: [],   // include all billing statuses
        // userRoles / technicianId can be added here if needed
      });

      // ─── Then use endorsement_form_id filter & grouping ───
      // const filteredJobOrders = filteredResults.filter(
      const filteredJobOrders = list.filter(
        jobOrder =>
          jobOrder.endorsement_form_id?.trim()
      );

      const groupedByEndorsementFormID = {};
      filteredJobOrders.forEach(jobOrder => {
        const {
          endorsement_form_id,
          technicians,
          endorsement_vehicle,
          endorsement_dispatch_time,
          endorsement_form_driver
        } = jobOrder;

        if (!groupedByEndorsementFormID[endorsement_form_id]) {
          groupedByEndorsementFormID[endorsement_form_id] = {
            endorsement_form_id,
            technicians,
            endorsement_vehicle,
            endorsement_dispatch_time,
            endorsement_form_driver,
            jobOrders: []
          };
        }

        groupedByEndorsementFormID[endorsement_form_id].jobOrders.push({
          ...jobOrder,
          job_types: Array.isArray(jobOrder.job_types) ? jobOrder.job_types : []
        });
      });

      const sortedGroups = groupByEndorsementIdAndSort(filteredJobOrders);
      setGroupedList(sortedGroups);      
      setShowRestDayCard(true);

      // Compute total job orders across all groups
      const total = sortedGroups.reduce((acc, group) => acc + group.jobOrders.length, 0);
      setTotalJobOrders(total);
    }

    setIsLoading(false);
  };


  // Clicking endorsement ID navigates to the edit page
  const handleEdit = (group) => {
    navigate('/edit-endorsement-form', { state: { group } });
  };
  
  // Full formatted date (e.g. Saturday, April 5, 2025)
  let fullFormattedDate = "";
  if (searchedDate) {
    fullFormattedDate = new Date(searchedDate).toLocaleDateString("en-US", { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  // For filtering technicians, we need the weekday from the searched date.
  const dayName = searchedDate
    ? new Date(searchedDate).toLocaleDateString("en-US", { weekday: "long" })
    : "";

  const filteredTechnicians = technicians.filter(
    (tech) => tech.rest_day && tech.rest_day.includes(dayName)
  );

  const technicianNames =
    filteredTechnicians.length > 0
      ? filteredTechnicians.map((tech) => `${tech.lastname}, ${tech.firstname}`).join("\n")
      : "No technicians found";

  // Global counter for job orders across all endorsement groups
  let globalJobOrderIndex = 0;

  
  return (
    <BaseWrapper
      titleIcon={<FactCheckIcon />}
      title="Operations"
      subTitle="Daily Forecast"
      subTitleRoute="/view-daily-forecast"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={4}>
              <TextField
                label="Date"
                name="date_filter"
                type="date"
                value={dateFilter || ''}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setSearchedDate(null);
                  setShowRestDayCard(false);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearchButtonClick}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <StyledHr />

      {/* Rest Day Card */}
      {showRestDayCard && searchedDate && (
        <Card variant="outlined" sx={{ marginBottom: 2, padding: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10}>
                {/* Date */}
                <Typography variant="h6" gutterBottom>
                  {fullFormattedDate}
                </Typography>

                {/* Total Job Orders Card (nested small blue box) */}
                <Card
                  sx={{
                    backgroundColor: '#1976D2',
                    color: '#FFF',
                    borderRadius: 1,
                    textAlign: 'center',
                    width: 180,
                    marginTop: 1,
                    boxShadow: 1,
                  }}
                >
                  <CardContent sx={{ padding: '4px !important' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                    >
                      Total Job Orders
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {totalJobOrders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Rest Day Technicians */}
              <Grid item xs={2}>
                <Typography variant="h6">On Rest Day:</Typography>
                {techLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-line" }}
                    color="textSecondary"
                  >
                    {technicianNames}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Endorsement groups - each card wrapped in a Box for extra spacing */}
      {groupedList.map((group) => (
        <Box key={group.endorsement_form_id} sx={{ mb: 6 }}>
          <Card 
            variant="outlined" 
            sx={{ 
                padding: 2,
                backgroundColor: theme.palette.grey[50]
                }}
            >
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={10}>                  
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.primary.main,
                      '&:hover': { color: theme.palette.primary.dark }
                    }}
                    onClick={() => handleEdit(group)}
                  >
                    Endorsement ID: {group.endorsement_form_id}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Technicians:{" "}
                    {group.technicians && group.technicians.length > 0 ? (
                      group.technicians.map((tech, idx) => (
                        <span key={tech.id}>
                          {idx > 0 && ', '}{tech.firstname} {tech.lastname}
                        </span>
                      ))
                    ) : (
                      "-"
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body1" gutterBottom>
                    Vehicle: {group?.endorsement_vehicle ? group.endorsement_vehicle : "-"}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Dispatch Time: {group?.endorsement_dispatch_time ? convertToAmPm(group.endorsement_dispatch_time) : "-"}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Driver: {group?.endorsement_form_driver ? group.endorsement_form_driver : "-"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Job orders rendered as Accordions */}
              {group.jobOrders.map((jobOrder) => {
                globalJobOrderIndex += 1;
                const clientAddress = jobOrder.client && jobOrder.client.address 
                  ? `${jobOrder.client.address.house_unit_street || ""}, ${jobOrder.client.address.barangay || ""}, ${jobOrder.client.address.city || ""}, ${jobOrder.client.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                  : "-";
                return (
                  <Accordion key={jobOrder.id} sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Grid container spacing={2} alignItems="flex-start">
                        {/* Left half: job order summary details */}
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Job Order {globalJobOrderIndex}
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>{jobOrder.client.name}</strong>
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Date: {formatJobDateToDDMMMYYYY(jobOrder.job_date)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Time: {convertToAmPm(jobOrder.start_time)} - {convertToAmPm(jobOrder.end_time)}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="subtitle2">Job Type:</Typography>
                            {Array.isArray(jobOrder.job_types) && jobOrder.job_types.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {jobOrder.job_types.map((type, idx) => (
                                  <Chip
                                    key={idx}
                                    label={type.replace(/_/g, " ")}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                No job type defined.
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        {/* Right half: client address and special notes in summary */}
                        <Grid item xs={6} sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Job Site Address:
                            </Typography>
                            <Typography variant="body2" gutterBottom color="textSecondary">
                                {clientAddress}
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                Special Notes:
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                {jobOrder.special_notes ? jobOrder.special_notes : "-"}
                            </Typography>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        {/* Left column: additional job order details */}
                        <Grid item xs={6}>
                          <Typography variant="subtitle2">Job Description:</Typography>
                          {Array.isArray(jobOrder.job_description) && jobOrder.job_description.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {jobOrder.job_description.map((item, idx) => (
                                <Chip key={idx} label={item.replace(/_/g, " ")} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                          <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Required Documents To Bring:
                          </Typography>
                          {Array.isArray(jobOrder.documents_to_bring) && jobOrder.documents_to_bring.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {jobOrder.documents_to_bring.map((doc, idx) => (
                                <Chip key={idx} label={doc.replace(/_/g, " ")} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                          <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Equipment To Bring:
                          </Typography>
                          {Array.isArray(jobOrder.equipment) && jobOrder.equipment.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {jobOrder.equipment.map((eq, idx) => (
                                <Chip key={idx} label={eq.replace(/_/g, " ")} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </Grid>
                        {/* Right column: additional job order info */}
                        <Grid item xs={6}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">Endorser Name:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.endorser_name ? jobOrder.endorser_name : "-"}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ mt: 1 }}>Current Status:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.current_status ? jobOrder.current_status.replace(/_/g, " ") : "-"}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ mt: 1 }}>Schedule Confirmed With Clients:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.schedule_confirmed ? jobOrder.schedule_confirmed : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">Endorser Contact Number:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.endorser_contact_number ? jobOrder.endorser_contact_number : "-"}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ mt: 1 }}>Payment Method:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.payment_method ? jobOrder.payment_method : "-"}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ mt: 1 }}>With Client Complaints:</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {jobOrder.client_complaints ? jobOrder.client_complaints : "-"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </CardContent>
          </Card>
        </Box>
      ))}
    </BaseWrapper>
  );
}

export default ViewDailyForecast;
